import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import applicationService from "../../services/applicationService";
import jobOfferService from "../../services/jobOfferService";
import { getErrorMessage } from "../../utils/errorHandler";
import FormCard from "../../components/ui/FormCard";
import FormSection from "../../components/ui/FormSection";
import FormActions from "../../components/ui/FormActions";
import Breadcrumb from "../../components/ui/Breadcrumb";
import "../../styles/FormCard.css";

const ApplicationEdit = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();
  const [jobOffers, setJobOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    job_offer_id: "",
    cover_letter: "",
    status: "PENDING",
    notes: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [appResponse, offersResponse] = await Promise.all([
        applicationService.getById(id),
        jobOfferService.getAll(),
      ]);
      
      const application = appResponse?.data || appResponse;
      const offersList = Array.isArray(offersResponse) ? offersResponse : (offersResponse?.data || []);
      
      setJobOffers(offersList);
      setFormData({
        first_name: application.first_name || "",
        last_name: application.last_name || "",
        email: application.email || "",
        phone: application.phone || "",
        job_offer_id: application.job_offer_id || "",
        cover_letter: application.cover_letter || "",
        status: application.status || "PENDING",
        notes: application.notes || "",
      });
    } catch (err) {
      console.error("Error loading data:", err);
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = "Le prénom est requis";
    if (!formData.last_name.trim()) newErrors.last_name = "Le nom est requis";
    if (!formData.email.trim()) newErrors.email = "L'email est requis";
    if (!formData.phone.trim()) newErrors.phone = "Le téléphone est requis";
    if (!formData.job_offer_id) newErrors.job_offer_id = "L'offre d'emploi est requise";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSubmitting(true);
      await applicationService.update(id, formData);
      toast.success("Candidature mise à jour avec succès");
      navigate("/rh/applications");
    } catch (err) {
      console.error("Error updating application:", err);
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/rh/applications");
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: "RH", to: "/rh/dashboard" },
    { label: "Candidatures", to: "/rh/applications" },
    { label: "Modifier la candidature" }
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Breadcrumb items={breadcrumbItems} />
      <FormCard title="Modifier la candidature" showBackButton onBack={handleCancel}>
        <form onSubmit={handleSubmit}>
          <FormSection title="Informations du candidat">
            <div className="form-group">
              <label>Prénom *</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className={errors.first_name ? "error" : ""}
              />
              {errors.first_name && <span className="error">{errors.first_name}</span>}
            </div>
            <div className="form-group">
              <label>Nom *</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className={errors.last_name ? "error" : ""}
              />
              {errors.last_name && <span className="error">{errors.last_name}</span>}
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? "error" : ""}
              />
              {errors.email && <span className="error">{errors.email}</span>}
            </div>
            <div className="form-group">
              <label>Téléphone *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={errors.phone ? "error" : ""}
              />
              {errors.phone && <span className="error">{errors.phone}</span>}
            </div>
          </FormSection>

          <FormSection title="Détails de la candidature">
            <div className="form-group">
              <label>Offre d'emploi *</label>
              <select
                name="job_offer_id"
                value={formData.job_offer_id}
                onChange={handleChange}
                className={errors.job_offer_id ? "error" : ""}
              >
                <option value="">Sélectionner une offre</option>
                {jobOffers.map(offer => (
                  <option key={offer.id} value={offer.id}>
                    {offer.title}
                  </option>
                ))}
              </select>
              {errors.job_offer_id && <span className="error">{errors.job_offer_id}</span>}
            </div>
            <div className="form-group">
              <label>Lettre de motivation</label>
              <textarea
                name="cover_letter"
                value={formData.cover_letter}
                onChange={handleChange}
                placeholder="Lettre de motivation..."
              />
            </div>
            <div className="form-group">
              <label>Statut</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="PENDING">En attente</option>
                <option value="PRESELECTED">Présélectionné</option>
                <option value="INTERVIEW">Entretien</option>
                <option value="ACCEPTED">Accepté</option>
                <option value="REJECTED">Refusé</option>
              </select>
            </div>
            <div className="form-group">
              <label>Notes internes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Notes internes..."
              />
            </div>
          </FormSection>

          <FormActions
            onCancel={handleCancel}
            onSave={handleSubmit}
            saveText="Enregistrer les modifications"
            isLoading={submitting}
          />
        </form>
      </FormCard>
    </div>
  );
};

export default ApplicationEdit;

